import { PASSWORD_REGEX } from '@/pages/accounts/config/settings.constants'
import * as v from '@/types/valibot.types'

export const UpdateInformationRequestSchema = v.object({
  firstName: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
  lastName: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
  ianaTimeZone: v.pipe(v.string(), v.minLength(1), v.maxLength(50))
})

export type UpdateInformationRequest = v.InferOutput<
  typeof UpdateInformationRequestSchema
>

export const UpdateInformationResponseSchema = v.object({
  matched: v.boolean(),
  coParentName: v.nullable(v.string())
})

export type UpdateInformationResponse = v.InferOutput<
  typeof UpdateInformationResponseSchema
>

export const CreateAccountRequestSchema = v.object({
  pendingSignupGuid: v.pipe(v.string(), v.minLength(1)),
  code: v.pipe(v.string(), v.minLength(1)),
  firstName: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
  lastName: v.pipe(v.string(), v.minLength(1), v.maxLength(100)),
  password: v.pipe(v.string(), v.minLength(1), v.regex(PASSWORD_REGEX)),
  termsOfServiceAgreed: v.boolean(),
  userTimeZone: v.pipe(v.string(), v.minLength(1), v.maxLength(50)),
  rewardfulReferralId: v.nullable(v.string()),
  appId: v.pipe(v.string(), v.minLength(1))
})

export type CreateAccountRequest = v.InferOutput<
  typeof CreateAccountRequestSchema
>

export const CreateAccountResponseSchema = v.object({
  authToken: v.string(),
  email: v.string(),
  matchCode: v.string(),
  userID: v.number()
})

export type CreateAccountResponse = v.InferOutput<
  typeof CreateAccountResponseSchema
>

// Phone Verification & MFA

export const SendVerificationCodeRequestSchema = v.object({
  phoneNumber: v.pipe(v.string(), v.minLength(1))
})

export type SendVerificationCodeRequest = v.InferOutput<
  typeof SendVerificationCodeRequestSchema
>

export const SendVerificationCodeResponseSchema = v.string()

export type SendVerificationCodeResponse = v.InferOutput<
  typeof SendVerificationCodeResponseSchema
>

export const ConfirmVerificationCodeRequestSchema = v.object({
  verifyCode: v.pipe(v.string(), v.minLength(1))
})

export type ConfirmVerificationCodeRequest = v.InferOutput<
  typeof ConfirmVerificationCodeRequestSchema
>

// The unified PUT returns an empty success body, so '', null and undefined are all valid
export const ConfirmVerificationCodeResponseSchema = v.nullish(v.string())

export type ConfirmVerificationCodeResponse = v.InferOutput<
  typeof ConfirmVerificationCodeResponseSchema
>

export const ConfirmEnableMfaSmsRequestSchema = v.object({
  code: v.pipe(v.string(), v.minLength(1)),
  useBiometrics: v.boolean()
})

export type ConfirmEnableMfaSmsRequest = v.InferOutput<
  typeof ConfirmEnableMfaSmsRequestSchema
>

export const RefreshTokenSchema = v.object({
  token: v.string(),
  expiration: v.nullable(v.string()),
  isExpired: v.boolean(),
  tokenID: v.number()
})

export type RefreshToken = v.InferOutput<typeof RefreshTokenSchema>

export const BiometricTokenSchema = v.object({
  token: v.string(),
  expiration: v.nullable(v.string()),
  isExpired: v.boolean()
})

export type BiometricToken = v.InferOutput<typeof BiometricTokenSchema>

export const ConfirmEnableMfaSmsResponseSchema = v.object({
  newToken: v.string(),
  newRefreshToken: v.custom<RefreshToken>(() => true),
  newBiometricToken: v.custom<BiometricToken>(() => true),
  mfaCookie: v.string()
})

export type ConfirmEnableMfaSmsResponse = v.InferOutput<
  typeof ConfirmEnableMfaSmsResponseSchema
>

export const DisableMfaSmsRequestSchema = v.object({
  useBiometrics: v.boolean()
})

export type DisableMfaSmsRequest = v.InferOutput<
  typeof DisableMfaSmsRequestSchema
>

export const DisableMfaSmsResponseSchema = v.object({
  newToken: v.string(),
  newRefreshToken: v.custom<RefreshToken>(() => true),
  newBiometricToken: v.custom<BiometricToken>(() => true)
})

export type DisableMfaSmsResponse = v.InferOutput<
  typeof DisableMfaSmsResponseSchema
>

export interface SignupLocale {
  signout: string
  deleteAccount: string
  loginExisting: string
  contactUs: string
  title: string
  metaDesc: string
  next: string
  nextSrStart: string
  nextSrEnd: string
  accountDisclaimer: string
  sectionTitle: {
    create: string
    invite: string
    match: string
    details: string
    security: string
  }
  email: {
    label: string
    error: string
    matchIdPopper: { label: string; content: string }
    verifyModal: {
      title: string
      content: string
      verifyCta: string
      verifySrText: string
      anotherCodeCta: string
      resentText: string
      resendFail: string
      support: string
    }
    validation: { required: string; invalid: string }
    heading: string
    headingEmphasis: string
    benefits: { organize: string; coordinate: string; peace: string }
    submitButton: string
    disclaimer: string
    disclaimerTos: string
    disclaimerPrivacyPolicy: string
  }
  info: {
    firstName: { label: string; error: string }
    lastName: { label: string; error: string }
    password: {
      label: string
      requirementsError: string
      emptyError: string
      hint: string
    }
    passwordConfirm: { label: string; matchError: string; emptyError: string }
    timezone: { label: string; error: string; title: string }
    tos: {
      label: string
      error: string
      tosLink: string
      privacyPolicyLink: string
    }
    next: string
    nextSr: string
    createAccount: string
    submitButton: string
  }
  security: {
    additionalSecurity: string
    sendCodeText: string
    phone: { label: string; error: string }
    sendCodeCta: string
    skip: string
    mfaModal: { title: string; submitText: string }
  }
  matchId: {
    title: string
    description: string
    yesCta: string
    skipCta: string
    verify: {
      modalTitle: string
      title: string
      description: string
      verifyCta: string
      verifySr: string
      error: string
    }
  }
  emailMatch: {
    title: string
    description: string
    yesCta: string
    skipCta: string
    modal: {
      title: string
      coparentEmail: string
      nextCta: string
      nextSrText: string
      noMatchError: string
      sameEmailError: string
      emailRequired: string
      emailInvalid: string
    }
  }
  infoMatch: {
    coparentInfo: {
      title: string
      titleAlt: string
      firstName: { label: string; labelAlt: string; error: string }
      lastName: { label: string; labelAlt: string; error: string }
      email: { label: string; labelAlt: string }
      saveCta: string
      saveCtaSr: string
    }
    child: {
      title: string
      titleAlt: string
      subtitle: string
      firstName: { label: string; labelAlt: string; error: string }
      dob: {
        label: string
        labelAlt: string
        error: string
        placeholder: string
      }
      saveCta: string
      saveCtaSr: string
    }
    nextStepCta: string
    summary: {
      description: string
      matchMethods: {
        label: string
        dropdownLabel: string
        invite: {
          title: string
          options: {
            label: string
            placeholder: string
            email: {
              optionLabel: string
              inputLabel: string
              sendCta: string
              inviteSuccess: string
              emailRequired: string
              emailInvalid: string
            }
            text: {
              optionLabel: string
              inputLabel: string
              sendCta: string
              inviteSuccess: string
              phoneRequired: string
              phoneInvalid: string
            }
            matchId: {
              optionLabel: string
              inputLabel: string
              copyCta: string
              copySuccess: string
            }
          }
          methodTitle: string
        }
        matchId: string
      }
      viewDetails: string
      editSr: string
    }
  }
  accountDetails: {
    accountStatus: string
    pendingMatch: string
    matchId: { label: string; copyCta: string; openVerifyModal: string }
    accountInfo: {
      title: string
      matched: string
      accountCreated: string
      tzDateFormat: string
      editAccountInfoModal: {
        title: string
        save: string
        srSaveAccountInfo: string
        successToast: string
      }
      notMatched: string
    }
    generalInfo: {
      title: string
      name: string
      email: string
      tzDateFormat: string
      editAccountInfoModal: {
        title: string
        save: string
        srSaveAccountInfo: string
        successToast: string
      }
    }
    security: {
      title: string
      password: {
        label: string
        editModal: {
          title: string
          currentPassword: string
          newPassword: string
          newPasswordHint: string
          confirmNewPassword: string
          currentPasswordEmptyError: string
          currentPasswordError: string
          noMatchError: string
          passwordReqError: string
          save: string
          successToast: string
        }
        editSr: string
      }
      phone: {
        label: string
        unverifiedCta: string
        editModal: {
          title: string
          labelHint: string
          savePhone: string
          srSavePhone: string
          verifyPhoneModalTitle: string
          verifyPhoneButtonText: string
          verifyError: string
          successToastAdded: string
          successToastUpdated: string
          placeholder: string
        }
        noPhoneProvided: string
        editSr: string
        error: string
        verifiedToast: string
        verificationCodeResentToast: string
      }
      mfa: {
        label: string
        unverifiedLabel: string
        description: string
        unverifiedDescription: string
        modal: {
          title: string
          successToastEnable: string
          successToastDisable: string
        }
      }
    }
    coparent: {
      title: string
      editModalTitle: string
      fullName: string
      email: string
      sendInvite: string
      successToast: string
    }
    child: {
      title: string
      editModalTitle: string
      firstName: string
      dob: string
      successToast: string
    }
    deleteAccountConfirm: {
      title: string
      text: string
      matchedAccountError: string
      cta: string
    }
    accountDeleted: { title: string; text: string }
  }
  matchSuccess: {
    title: string
    matchedWith: string
    redirectText: string
    cantWait: string
    bypassRedirect: string
  }
  signInLink: { prompt: string; action: string }
  professionalLink: { prompt: string; action: string }
  phone: {
    validation: {
      invalidCountryCode: string
      tooShort: string
      tooLong: string
      localOnly: string
      invalidLength: string
    }
  }
  header: { goBack: string; logoTitle: string }
  verifyPhone: {
    phone: {
      error: string
      label: string
      sendCodeDescription: string
      enterCodeDescription: string
      editPhoneNumber: string
      sendCode: string
      verify: string
      sendAnotherCode: string
    }
    code: { error: string; minLengthError: string }
  }
  errors: {
    updateInformation: string
    createAccount: string
    sendVerificationCode: string
    confirmVerificationCode: string
    disableMfa: string
  }
}
